import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, RotateCcw, ImageOff } from 'lucide-react';
import { scryfallSrcSet } from './cardImageSources';
import { isLandscape, ROTATABLE } from './cardOrientation';
import './CardImage.css';

/**
 * Baustein — CardImage.
 * DIE Kartendarstellung des Design-Systems: Jedes im UI gezeigte Kartenbild
 * rendert über dieses Primitiv (direkt oder über Hüllen wie `CardTile.Image`
 * und `DetailSidePanel`).
 *
 * Eigenschaften:
 *   - 5:7-Seitenverhältnis, füllt die Breite seines Containers (flexible Größe).
 *   - Proportionaler Ecken-Radius (4.75% / 3.4%, skaliert mit der Kartengröße
 *     wie bei der physischen Karte) — CardImage bleibt die einzige Quelle der
 *     Wahrheit für den Karten-Eckenradius, app-weit einheitlich.
 *   - Rückseite vorhanden (`backSrc`) → 3D-Rotation. Der Flip-Button steht nur,
 *     wenn jemand die Drehung entgegennimmt: ungesteuert (ohne `face`) dreht er
 *     die Karte selbst, gesteuert nur mit `onFlip`. Einseitige Karten zeigen
 *     keinen Button.
 *   - `face` ('front' | 'back') → gesteuerte Seite: die Komponente zeigt genau
 *     diese Seite, ein interner Flip-Zustand wird nicht benutzt. Mit `onFlip`
 *     ruft der Button nur `onFlip()` und dreht nicht selbst; erst ein neues
 *     `face` von außen dreht die Karte (z.B. Playtest: der Server-Zustand
 *     entscheidet die sichtbare Seite). Ohne `onFlip` ist `face` eine reine
 *     Anzeige ohne Button. `face="back"` ohne `backSrc` zeigt die Vorderseite,
 *     eine andere gibt es nicht. Ohne `face` ruft der Button zusätzlich `onFlip`,
 *     falls gesetzt.
 *   - `defaultFace` ('front' | 'back', Default 'front') → Start-Seite des
 *     ungesteuerten Zustands; wird bei Wechsel von `src`, `backSrc` oder
 *     `defaultFace` erneut angewandt. Der Button dreht danach nur lokal.
 *   - Ein Druck auf den Flip-Button (`pointerdown`, `mousedown`, `touchstart`,
 *     `click`) erreicht nie die umgebende Karte: deren eigene Druck-Handler
 *     würden sonst die Hover-Vorschau schließen, die Karte nach vorn holen oder
 *     einen Zug starten (dnd-kits Touch-Sensor lauscht auf `touchstart`). Folge:
 *     ein Zug im Deck-Editor kann nicht auf dem Button beginnen. Menüs, die beim
 *     Druck außerhalb schließen, lauschen deshalb in der Capture-Phase.
 *   - Kaputte Bild-URL → Platzhalter (onError).
 *   - Bildgröße nach Darstellungsbreite: für Scryfall-URLs der `normal`-Form bekommen beide
 *     `<img>` ein `srcset` mit `small`/`normal`/`large` (`ui/cardImageSources`) und `sizes`
 *     = größte gemessene Layout-Breite des Containers seit dem letzten `src`/`backSrc`-Wechsel
 *     (bei einer gedrehten Seite × 5/7, weil deren Bildbreite die kurze Kante der Fläche ist).
 *     Der Browser wählt daraus die kleinste scharfe Variante samt Pixeldichte; ein später
 *     vergrößertes Bild lädt die größere nach und zeigt das alte, bis sie da ist, ein
 *     verkleinertes lädt nie eine kleinere nach (`sizes` wächst nur). Beide Attribute
 *     schreibt ein Layout-Effekt bzw. der ResizeObserver direkt ans DOM, nie das JSX: ein
 *     `srcset` ohne `sizes` rechnet der Browser mit voller Fensterbreite und lädt `large`,
 *     und je Größenänderung soll kein React-Render entstehen (Brett-Zoom). Layout-Breite
 *     (`offsetWidth`), nicht `getBoundingClientRect`: getappte Brettkarten und
 *     die Flip-Rotation sind CSS-Transformationen und ändern die Bildgröße nicht. Ein
 *     `srcset` mit Breiten-Deskriptoren verdrängt `src`, darum wird es bei jedem
 *     `src`-Wechsel im selben Commit neu geschrieben. Ein Ladefehler mit `srcset` entfernt
 *     `srcset` und `sizes` und lädt `src` neu; erst ein Fehler ohne `srcset` zeigt den
 *     Platzhalter. `src` bleibt immer die übergebene URL.
 *   - `children` = Overlay-Bereich: Seiten legen eigene Zusatz-Elemente über
 *     die Karte (absolut positioniert gegen den Karten-Container), z.B. später
 *     ein Drei-Punkte-Menü im Deck-Editor. Der Flip-Button selbst ist
 *     Grundausstattung und kein Overlay-Kind.
 *   - Ein CardImage darf NIE in einem <button> stecken: Flip-Button und
 *     Overlay-Kinder sind selbst interaktiv, ein umschließender Button wäre
 *     ein Button im Button (ungültiges HTML, kaputte Tastatur-/Screenreader-
 *     Bedienung). Klickbare Container lösen das wie `ui/CardTile` über ein
 *     gestrecktes Geschwister-<button>.
 *   - `onOpen` → ein unsichtbares, über die Karte gestrecktes Geschwister-<button>
 *     (`card-image__open`, z-index 1, unter Flip-Button 2 und Overlays 3) öffnet das
 *     Karten-Modal; `openLabel` ist sein zugänglicher Name (Default `alt`). Ohne `onOpen`
 *     kein Button. In `CardTile` NICHT setzen — die Kachel hat ihr eigenes Klick-Ziel.
 *
 * Querformat-Karten (Spec docs/superpowers/specs/2026-10-31-querformat-karten-drehen-design.md §6):
 *   - `orientation` ist der Wert `imageOrientation` aus dem Umschlag (`ui/cardOrientation`):
 *     Scryfall speichert jedes Bild hochkant, gedreht wird hier. Drehrichtung je Wert:
 *     `landscape`/`landscape_front` 90° im Uhrzeigersinn, `aftermath` 90° gegen den
 *     Uhrzeigersinn, `flip` 180°.
 *   - `landscape` wählt, was mit einer queren Seite passiert (Default `'never'`: nichts):
 *     `'always'` (Modal, Viewer, Seitenpanel) und `'preview'` (Hover-Vorschauen) machen die
 *     Fläche 7:5 (`card-image--landscape`) und legen die Seite gedreht hinein
 *     (`card-image__face--rotated`); welches Format die Fläche hat, entscheidet die gerade
 *     sichtbare Seite — bei einem Battle vorn quer, hinten hochkant. `'preview'` ist
 *     `'always'` ohne Dreh-Knopf. `'peek'` (Raster) lässt die Karte stehen und legt bei
 *     ruhender Maus ein gedrehtes Bild als Portal darüber (`card-image-peek`, am Ende des
 *     Dokuments, `pointer-events: none`, ohne Overlays, `aria-hidden`); nie wird etwas
 *     abgeschnitten oder verdeckt die Künstler-/©-Zeile.
 *   - `peek` ist das Objekt `{ hovering, resting }` aus `ui/useCardPeek` (der Konsument hält
 *     den Hook, weil in `CardTile` die Zeiger-Ereignisse das Kachel-Klick-Ziel treffen, nicht
 *     das Bild): `resting` = Maus ruht seit 200 ms auf dem Bild, nicht auf einem
 *     Bedienelement; `hovering` = Maus irgendwo über der Karte. Das Portal erscheint bei
 *     `resting` und querer sichtbarer Seite.
 *   - Dreh-Knopf „Karte drehen" (`card-image__rotate-btn`, oben links wie der Umdreh-Knopf)
 *     bei `orientation` `aftermath` (−90°, in `'always'` in die Querfläche, in `'peek'` als
 *     sofort gezeigtes Portal auch über dem Knopf) und `flip` (180° an Ort und Stelle), in
 *     den Modi `'peek'` und `'always'`. Gesteuert wie der Umdreh-Knopf: ohne `rotated` hält
 *     CardImage den Zustand selbst und setzt ihn beim Wechsel von `src` zurück; mit `rotated`
 *     zeigt es genau diesen Zustand, und der Knopf steht nur mit `onRotate`. Im Modus
 *     `'peek'` setzt bei Aftermath das Ende von `hovering` und der Fokusverlust des Knopfs
 *     den Zustand zurück (das Portal liegt über Nachbarn und darf nicht kleben); eine
 *     Flip-Karte bleibt gedreht, bis der Knopf erneut gedrückt wird.
 *   - Umdreh- und Dreh-Knopf tragen `data-card-control` und `card-image__control`, damit
 *     `useCardPeek` sie als Bedienelement erkennt und `CardTile.css` sie unter dem
 *     Kachel-Klick-Ziel freigibt.
 *
 * `CardImage.Back` — Kartenrücken für verdeckte Karten und Stapel (Playtest-
 * Bibliothek): dieselbe 5:7-Fläche mit demselben Ecken-Radius, aber kein Bild,
 * sondern ein Rautenmuster in den `--playtest-*`-Farben; Muster und Schein der Mittel-Raute
 * wachsen mit der Kartenbreite (Größen-Container). `role="img"`, zugänglicher
 * Name über `label` (Default „Kartenrücken"). `children` liegen wie bei CardImage
 * als Overlay über der Fläche (z.B. ein Zähler auf dem Bibliotheks-Stapel).
 *
 * Beispiel:
 *   <CardImage src={card.imageUrl} backSrc={card.imageUrlBack} alt={card.name} />
 *   <CardImage src={card.imageUrl} backSrc={card.imageUrlBack} alt={card.name}
 *     face={card.transformedFace} onFlip={() => sendFlip(card.id)} />
 *   <CardImage src={card.imageUrl} backSrc={card.imageUrlBack} alt={card.name} defaultFace="back" />
 *   <CardImage src={card.imageUrl} alt={card.name} landscape="always" orientation={card.imageOrientation} />
 *   <CardImage src={card.imageUrl} alt={card.name} landscape="peek" peek={peek} orientation={card.imageOrientation} />
 *   <CardImage.Back label="Verdeckte Karte" />
 */
function CardImageFallback() {
  return (
    <div className="card-image__fallback" data-testid="card-image-fallback">
      <ImageOff size={28} aria-hidden />
    </div>
  );
}

const LANDSCAPE_SIZES_FACTOR = 5 / 7;
const PEEK_VIEWPORT_MARGIN_PX = 8;

// Ohne matchMedia (jsdom) gilt das Gerät als hover-fähig.
function canHover() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(hover: none)').matches;
}

// Das gedrehte Bild des Rasters: eigenes <img> als Portal, mittig über der Karte, mit der
// Kartenhöhe als Bildbreite. Gemessen wird beim Erscheinen und, solange das Portal steht, nach
// jedem Ende einer Transition (die Stapelansicht schiebt die gehoverte Karte noch, wenn die
// Verweilzeit abläuft), beim Scrollen und bei Fenstergröße — so bleibt das Bild an der Karte,
// auch wenn der Konsument `hovering` stehen lässt (gedrehte Aftermath-Karte). Passiver Effekt,
// kein Layout-Effekt: erscheint das Portal im selben Commit wie die Karte, ist der Ref des
// Containers im Layout-Effekt des Kinds noch nicht gesetzt (React hängt Refs kindaufwärts an).
function CardPeekImage({ src, containerRef, direction }) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const next = containerRef.current.getBoundingClientRect();
      setRect((prev) => (prev && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height ? prev : next));
    };
    measure();
    document.addEventListener('transitionend', measure, true);
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    window.addEventListener('resize', measure);
    return () => {
      document.removeEventListener('transitionend', measure, true);
      window.removeEventListener('scroll', measure, { capture: true });
      window.removeEventListener('resize', measure);
    };
  }, [containerRef]);
  if (!rect) return null;
  const srcset = scryfallSrcSet(src);
  // Mittig über der Karte, aber ins Sichtfenster geklemmt: am Fensterrand ragte die lange Kante
  // sonst hinaus, und ein abgeschnittenes Kartenbild verstößt gegen die Scryfall-Regeln.
  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
  const width = rect.height;
  const height = rect.width;
  const left = clamp(rect.left + rect.width / 2 - width / 2, PEEK_VIEWPORT_MARGIN_PX, Math.max(PEEK_VIEWPORT_MARGIN_PX, window.innerWidth - width - PEEK_VIEWPORT_MARGIN_PX));
  const top = clamp(rect.top + rect.height / 2 - height / 2, PEEK_VIEWPORT_MARGIN_PX, Math.max(PEEK_VIEWPORT_MARGIN_PX, window.innerHeight - height - PEEK_VIEWPORT_MARGIN_PX));
  return createPortal(
    <div
      className="card-image-peek"
      data-testid="card-image-peek"
      data-direction={direction}
      aria-hidden
      style={{ left, top, width, height }}
    >
      <img
        src={src}
        srcSet={srcset || undefined}
        sizes={srcset ? `${Math.round(rect.height)}px` : undefined}
        alt=""
        aria-hidden="true"
        draggable={false}
        style={{ width: rect.width, height: rect.height }}
      />
    </div>,
    document.body
  );
}

export default function CardImage({
  src, backSrc, alt = '', onOpen, openLabel, face, defaultFace = 'front', onFlip,
  orientation, landscape = 'never', peek, rotated, onRotate, children,
}) {
  const [flipped, setFlipped] = useState(defaultFace === 'back');
  const [rotatedState, setRotatedState] = useState(false);
  const [frontError, setFrontError] = useState(false);
  const [backError, setBackError] = useState(false);
  const hasBack = !!(backSrc && String(backSrc).trim() !== '');
  const pointerDownRef = useRef(null);
  const containerRef = useRef(null);
  const maxWidthRef = useRef(0);
  const droppedRef = useRef({ front: false, back: false });
  const sourcesRef = useRef({ src, backSrc });
  const rotatedSidesRef = useRef({ front: false, back: false });
  const prevHoveringRef = useRef(false);
  const controlled = face === 'front' || face === 'back';
  const showsBack = hasBack && (controlled ? face === 'back' : flipped);
  const showsFlipButton = hasBack && (!controlled || typeof onFlip === 'function');

  const rotatedControlled = typeof rotated === 'boolean';
  const isRotated = rotatedControlled ? rotated : rotatedState;
  // Im Raster zeigt Aftermath das gedrehte Bild nur bei einer ruhenden Maus (`peek.hovering`);
  // ohne Hover-Fähigkeit (Touch) wäre der Knopf dort ein Bedienelement ohne Wirkung.
  const showsRotateButton = ROTATABLE.has(orientation)
    && (landscape === 'peek' || landscape === 'always')
    && (!rotatedControlled || typeof onRotate === 'function')
    && !(landscape === 'peek' && orientation === 'aftermath' && !canHover());
  const areaMode = landscape === 'always' || landscape === 'preview';
  const faceLandscape = isLandscape(orientation, showsBack ? 'back' : 'front');
  const aftermathRotated = isRotated && orientation === 'aftermath';
  const areaLandscape = areaMode && (faceLandscape || (landscape === 'always' && aftermathRotated));
  const rotatedFront = areaMode && isLandscape(orientation, 'front');
  const rotatedBack = areaMode && isLandscape(orientation, 'back');
  const ccwFront = landscape === 'always' && aftermathRotated;
  const upsideFront = (landscape === 'always' || landscape === 'peek') && isRotated && orientation === 'flip';
  const showsPeek = landscape === 'peek' && !!peek
    && ((peek.resting && faceLandscape) || (aftermathRotated && peek.hovering));
  // Nur bei Aftermath im Raster klebt ein Dreh-Zustand sichtbar über Nachbarn, deshalb nur dort
  // das Zurücksetzen beim Verlassen und beim Fokusverlust des Knopfs.
  const peekResets = landscape === 'peek' && orientation === 'aftermath' && !rotatedControlled;

  // Zeigt dieselbe Instanz eine andere Karte (z.B. Detail-Panel bei
  // Auswahl-Wechsel), dürfen Flip-, Dreh- und Fehler-Zustand nicht kleben bleiben —
  // sonst bleibt die Anzeige rückseitig gedreht, obwohl es keine Rückseite gibt.
  useEffect(() => {
    setFlipped(defaultFace === 'back');
    setRotatedState(false);
    setFrontError(false);
    setBackError(false);
  }, [src, backSrc, defaultFace]);

  useEffect(() => {
    const hovering = !!peek?.hovering;
    if (peekResets && prevHoveringRef.current && !hovering) setRotatedState(false);
    prevHoveringRef.current = hovering;
  }, [peek, peekResets]);

  // Liest nur Refs und bleibt deshalb über alle Renders dieselbe Funktion. Jedes Attribut wird
  // nur bei einer Änderung geschrieben: auch ein unverändertes `srcset` ließe den Browser die
  // Quellenwahl neu anstoßen, und beim Brett-Zoom kommt je Karte eine Messung pro Tick.
  const applySizing = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const width = maxWidthRef.current;
    const write = (img, name, value) => { if (img.getAttribute(name) !== value) img.setAttribute(name, value); };
    container.querySelectorAll('img[data-face]').forEach((img) => {
      const side = img.dataset.face;
      const srcset = scryfallSrcSet(side === 'back' ? sourcesRef.current.backSrc : sourcesRef.current.src);
      if (width > 0 && srcset && !droppedRef.current[side]) {
        const imageWidth = rotatedSidesRef.current[side] ? Math.round(width * LANDSCAPE_SIZES_FACTOR) : width;
        write(img, 'srcset', srcset);
        write(img, 'sizes', `${imageWidth}px`);
      } else {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      }
    });
  }, []);

  // Vor den Mess-Effekten deklariert: sie lesen Quellen und Drehung über die Refs im selben Commit.
  const frontSideRotated = rotatedFront || ccwFront;
  useLayoutEffect(() => {
    sourcesRef.current = { src, backSrc };
    rotatedSidesRef.current = { front: frontSideRotated, back: rotatedBack };
  });

  useLayoutEffect(() => {
    maxWidthRef.current = 0;
    droppedRef.current = { front: false, back: false };
    const width = containerRef.current ? containerRef.current.offsetWidth : 0;
    if (width > 0) maxWidthRef.current = width;
    applySizing();
  }, [src, backSrc, applySizing]);

  // Ein Fehler tauscht das <img> gegen den Platzhalter, ein neues src hängt es wieder ein —
  // das neue Element braucht die Attribute im selben Commit; eine gedrehte Seite ändert `sizes`.
  useLayoutEffect(() => {
    applySizing();
  }, [frontError, backError, hasBack, frontSideRotated, rotatedBack, applySizing]);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !containerRef.current) return undefined;
    // Der Beobachter meldet nur „Größe geändert"; gemessen wird die Layout-Breite des
    // Containers selbst, nicht der Eintrag — so bleibt die Messung an einer Stelle.
    const observer = new ResizeObserver(() => {
      const width = containerRef.current ? containerRef.current.offsetWidth : 0;
      if (width <= maxWidthRef.current) return;
      maxWidthRef.current = width;
      applySizing();
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [applySizing]);

  const onImageError = (side, setError) => (e) => {
    const img = e.currentTarget;
    if (img.hasAttribute('srcset')) {
      droppedRef.current[side] = true;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      return;
    }
    setError(true);
  };

  const faceClass = (base, rotatedSide, ccw, upside) => [
    'card-image__face', base,
    rotatedSide && 'card-image__face--rotated',
    ccw && 'card-image__face--rotated-ccw',
    upside && 'card-image__face--upside',
  ].filter(Boolean).join(' ');

  const containerClass = [
    'card-image',
    showsBack && 'card-image--flipped',
    areaLandscape && 'card-image--landscape',
  ].filter(Boolean).join(' ');

  // Die Knöpfe halten jeden Druck an: ein Druck auf einen Knopf ist kein Griff an die Karte.
  const controlProps = {
    onPointerDown: (e) => e.stopPropagation(),
    onMouseDown: (e) => e.stopPropagation(),
    onTouchStart: (e) => e.stopPropagation(),
  };

  return (
    <div ref={containerRef} className={containerClass}>
      <div className="card-image__flip-inner">
        <div className={faceClass('card-image__face--front', rotatedFront, ccwFront, upsideFront)}>
          {frontError ? <CardImageFallback /> : (
            <img src={src} alt={alt} loading="lazy" draggable={false} data-face="front" onError={onImageError('front', setFrontError)} />
          )}
        </div>
        {hasBack && (
          <div className={faceClass('card-image__face--back', rotatedBack, false, false)}>
            {backError ? <CardImageFallback /> : (
              <img src={backSrc} alt={`${alt} (Rückseite)`} loading="lazy" draggable={false} data-face="back" onError={onImageError('back', setBackError)} />
            )}
          </div>
        )}
      </div>
      {typeof onOpen === 'function' && (
        <button
          type="button"
          className="card-image__open"
          aria-label={openLabel || alt}
          // Kein stopPropagation am Pointer-Down: darüber ziehen die Editor-Ansichten die Karte
          // (dnd-kit-Listener am Wrapper). Stattdessen merkt sich der Button den Startpunkt und
          // ignoriert den Klick, der am Ende eines Zugs entsteht (Snap-back über derselben Karte).
          onPointerDown={(e) => { pointerDownRef.current = { x: e.clientX, y: e.clientY }; }}
          onPointerCancel={() => { pointerDownRef.current = null; }}
          onClick={(e) => {
            e.stopPropagation();
            const start = pointerDownRef.current;
            pointerDownRef.current = null;
            // detail 0 = Tastatur-/AT-Aktivierung (keine echte Maus-Geste, clientX/Y liefen 0) —
            // die Zieh-Distanz-Prüfung greift nur bei echten Maus-Klicks (detail >= 1).
            if (e.detail !== 0 && start && (Math.abs(e.clientX - start.x) > 5 || Math.abs(e.clientY - start.y) > 5)) return;
            onOpen();
          }}
        />
      )}
      {showsFlipButton && (
        <button
          type="button"
          className="card-image__control card-image__flip-btn"
          data-card-control=""
          aria-label="Karte umdrehen"
          title="Karte umdrehen"
          {...controlProps}
          onClick={(e) => {
            e.stopPropagation();
            if (!controlled) setFlipped((f) => !f);
            if (typeof onFlip === 'function') onFlip();
          }}
        >
          <RefreshCw size={18} aria-hidden />
        </button>
      )}
      {showsRotateButton && (
        <button
          type="button"
          className="card-image__control card-image__rotate-btn"
          data-card-control=""
          aria-label="Karte drehen"
          title="Karte drehen"
          {...controlProps}
          onBlur={() => { if (peekResets) setRotatedState(false); }}
          onClick={(e) => {
            e.stopPropagation();
            if (!rotatedControlled) setRotatedState((r) => !r);
            if (typeof onRotate === 'function') onRotate();
          }}
        >
          <RotateCcw size={18} aria-hidden />
        </button>
      )}
      {children}
      {showsPeek && (
        <CardPeekImage
          src={showsBack ? backSrc : src}
          containerRef={containerRef}
          direction={aftermathRotated ? 'ccw' : 'cw'}
        />
      )}
    </div>
  );
}

CardImage.Back = function CardBack({ label = 'Kartenrücken', children }) {
  return (
    <div className="card-image card-image-back" role="img" aria-label={label}>
      {children}
    </div>
  );
};
